/**
 * Reusable phone number utilities using google-libphonenumber
 * Used across TUC processes components for consistent phone number handling
 */
import {PhoneNumberUtil, metadata as PhoneNumberUtilMetadata} from 'google-libphonenumber'

import {PhoneNumber} from '../types'

const phoneNumberUtil = PhoneNumberUtil.getInstance()

const supportedRegions: Array<string> = phoneNumberUtil.getSupportedRegions()

const {countryCodeToRegionCodeMap} = PhoneNumberUtilMetadata

export const createDialingCodesByCountryCodeMap = (regionsByCountryCode: {
  [key: string]: Array<string>
}) => {
  const supportedCountryCodes = Object.keys(regionsByCountryCode)

  const dialingCodesByCountryCode = supportedCountryCodes.reduce(
    (
      accumulator: {
        [key: string]: string
      },
      currentCallingCode: string
    ) => {
      const countryCodes = regionsByCountryCode[currentCallingCode]
      const dialingCodes = countryCodes.reduce(
        (map: {[key: string]: string}, countryCode: string) => {
          map[countryCode] = currentCallingCode
          return {
            ...map,
            [countryCode]: currentCallingCode
          }
        },
        {}
      )

      accumulator = {
        ...accumulator,
        ...dialingCodes
      }
      return accumulator
    },
    {}
  )

  return dialingCodesByCountryCode
}

// For better performance, create a map object. We will not need to filter the stuff later while mapping.
const dialingCodesByCountryCode = createDialingCodesByCountryCodeMap(countryCodeToRegionCodeMap)
export const regions = supportedRegions.map((countryCode) => {
  const dialingCode = `+${dialingCodesByCountryCode[countryCode]}`
  return {
    countryCode,
    dialingCode
  }
})

/**
 * Convert PhoneNumber object to string for display
 * Fixed to handle null dialingCode properly
 */
export const phoneNumberToString = (phoneNumber?: PhoneNumber): string => {
  if (!phoneNumber) {
    return ''
  }
  // Handle null dialingCode case - don't show "null"
  if (!phoneNumber.dialingCode || !phoneNumber.number) {
    return phoneNumber.number || ''
  }
  return `${phoneNumber.dialingCode}${phoneNumber.number}`
}

/**
 * Get dialing code from country code (ISO 2-letter code)
 * @param countryCode - ISO 2-letter country code (e.g., 'US', 'DE', 'GB')
 * @returns Dialing code with '+' prefix (e.g., '+1', '+49', '+44') or null if invalid
 */
export const getDialingCodeFromCountry = (countryCode: string): string | null => {
  try {
    const countryCallingCode = phoneNumberUtil.getCountryCodeForRegion(countryCode)

    if (countryCallingCode === 0) {
      // Invalid country code
      return null
    }

    return `+${countryCallingCode}`
  } catch (error) {
    console.warn('Error getting dialing code for country:', countryCode, error)
    return null
  }
}

/**
 * Enhanced version with validation
 */
export const getDialingCodeSafe = (countryCode?: string): string | null => {
  if (!countryCode || countryCode.length !== 2) {
    return null
  }

  return getDialingCodeFromCountry(countryCode.toUpperCase())
}

/**
 * Parse phone number string into PhoneNumber object
 */
export const parsePhoneNumber = (phoneNumber: string, defaultCountry: string): PhoneNumber => {
  const country: string = (() => {
    try {
      const number = phoneNumberUtil.parse(phoneNumber)
      return phoneNumberUtil.getRegionCodeForNumber(number)
    } catch (err) {
      return defaultCountry
    }
  })()

  const parsedNumber = (() => {
    if (isPhoneNumberValid(phoneNumber, country)) {
      return phoneNumberUtil.parse(phoneNumber, country)
    }
    return null
  })()

  const dialingCode = (() => {
    if (parsedNumber) {
      return `+${parsedNumber.getCountryCode()}`
    }
    // Try to get dialing code from regions - this would need regions import
    return getDialingCodeSafe(country)
  })()

  const number = (() => {
    if (parsedNumber) {
      return `${parsedNumber.getNationalNumber()}`
    }
    return ''
  })()

  return {
    dialingCode,
    number,
    country
  }
}

/**
 * Helper function to validate phone number format
 */
export const isPhoneNumberValid = (phoneNumber: string, country?: string): boolean => {
  try {
    const parsed = phoneNumberUtil.parse(phoneNumber, country)
    return phoneNumberUtil.isValidNumber(parsed)
  } catch (err) {
    return false
  }
}
